import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, forwardRef, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { LocaleSettings } from 'primeng/calendar';
import moment from 'moment';
import { MIN_DOCUMENT_DATE } from '../../../constants';
import { PrimeNGConfig } from 'primeng/api';
import { Utilities } from 'src/app/shared/utilities';

moment.locale('ru-RU');

@Component({
  selector: 'app-prime-calendar',
  templateUrl: './prime-calendar.component.html',
  styleUrls: ['./prime-calendar.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrimeCalendarComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class PrimeCalendarComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder: string;
  /** Формат даты (можно определить в LocaleSettings */
  @Input() dateFormat: string;
  @Input() date: any;
  /** Флаг о запрете ввода даты с клавиатуры */
  @Input() readonlyInput: boolean = false;
  /** Объект, имеющий региональные свойства конфигурации календаря */
  @Input() locale: LocaleSettings;
  @Input() disabled: any;
  @Input() utc: boolean = false;
  /** Формат даты на выходе */
  @Input() outputFormat: string = null;
  /**  */
  @Input() showButtonBar: boolean;
  /** Идентификатор поля ввода */
  @Input() inputId: string;
  /** Должен ли месяц отображаться в виде выпадающего списка вместо текста */
  @Input() monthNavigator: boolean;
  /** Должен ли год отображаться в виде выпадающего списка вместо текста */
  @Input() yearNavigator: boolean;
  /** Диапазон лет, отображаемый в раскрывающемся списке года в формате (nnnn: nnnn) */
  @Input() yearRange: string;
  /** Флаг отображения таймера */
  @Input() showTime: boolean = false;
  /** Флаг отображения только таймера */
  @Input() timeOnly: boolean = false;
  /** Шаг изменения минут */
  @Input() stepMinute: number = 1;
  /** Дата отображаемая при открытии, пока дата не выбрана */
  @Input() defaultDate: Date = null;
  /** Флаг о том что необходимо запретить выбор/ввод будущей даты */
  @Input() disableFutureDate: boolean = false;
  /** Минимальная выбираемая дата (нужно доработать логику вычисления) */
  @Input() minimumDate: Date = null;
  /** Максимальная выбираемая дата (нужно доработать логику вычисления) */
  @Input() maximumDate: Date = null;
  /** Формат маски поля ввода (date/time/dateTime) */
  @Input() formatInputMaskType: string = 'date';
  @Input() styleClass: string = '';
  /** Отображать иконку */
  @Input() showIcon: boolean = false;
  @Input() showSeconds: boolean = false;
  private today: Date;
  constructor(private config: PrimeNGConfig) {}

  ngOnInit(): void {
    this.config.setTranslation(this.locale);
  }
  /** Возвращает максимальную дату для выбора в календаре */
  public get maxDate() {
    if (this.disableFutureDate) {
      /** Текущая дата */
      const serverDateTime = moment().toDate();
      if (!this.today || moment(serverDateTime).isAfter(this.today, 'day')) {
        /** Если серверная дата не записана или больше записанной, то перезаписывает ее */
        this.today = serverDateTime;
      }
      return this.today;
    }
    return !Utilities.isNullOrUndefined(this.maximumDate) ? this.maximumDate : null;
  }

  public get minDate() {
    return !Utilities.isNullOrUndefined(this.minimumDate) ? this.minimumDate : null;
  }

  onChange: any = () => {};
  onToched: any = () => {};
  propagateChange = (_: any) => {};

  writeValue(obj: any): void {
    if (obj && typeof obj === 'string') {
      try {
        // Пытаемся сконвертировать в ISO
        this.date = new Date(obj);
      } catch {
        this.date = obj;
      }
    } else {
      this.date = obj;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onToched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleSelect(event: any) {
    if (this.utc) {
      const selected = <Date>event;
      const utc = Date.UTC(
        selected.getFullYear(),
        selected.getMonth(),
        selected.getDate(),
        selected.getHours(),
        selected.getMinutes(),
        selected.getSeconds(),
        selected.getMilliseconds()
      );

      this.onChange(new Date(utc));
      this.propagateChange(utc);
      return;
    }
    event = this.outputFormat ? moment(<Date>event).format(this.outputFormat) : event;

    this.onChange(<Date>event);
    this.propagateChange(<Date>event);
  }

  handleClear() {
    this.onChange(null);
    this.propagateChange(null);
  }

  onTodayClick(): void {
    this.date = this.defaultDate;
    this.onChange(this.defaultDate);
    this.propagateChange(this.defaultDate);
  }

  onBlur(event: any) {
    const isZeroYear = RegExp(/\d{2}.\d{2}.0\d{3}/);

    if (isZeroYear.test(event.target.value)) {
      this.date = null;
      event.target.value = '';
    }
    if (!this.date || moment(this.date).isBefore(MIN_DOCUMENT_DATE, 'day')) {
      if (event.target.value.length === 10) {
        if (moment(event.target.value, 'DD-MM-YYYY').isBefore(MIN_DOCUMENT_DATE, 'day')) {
          this.date = moment(MIN_DOCUMENT_DATE, 'YYYY-MM-DD').toDate();
        } else {
          this.date = moment(event.target.value, 'DD-MM-YYYY').toDate();
        }
        this.handleSelect(this.date);
      } else {
        this.handleClear();
      }
    }
    this.onToched();
  }

  // Функция для корректного обновления данных при валидации
  onInput(event: any) {
    if (this.date) {
      this.handleSelect(this.date);
    }
    if (event.target.value === '') {
      this.onChange(null);
    }
  }
}
